// OPT-HIST-02B — Ficha comercial de solo lectura. El archivo técnico no se expone en la UI.
import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backLabel, RETURN_PARAM } from '../modules/pricingJourney/navigation';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import { getPricingDecisionEvaluation } from '../modules/marginCenter/services/pricingDecisionService';
import OptimizationDecisionSection from '../modules/marginCenter/components/OptimizationDecisionSection';
import OptimizationApplicationSection from '../modules/marginCenter/components/OptimizationApplicationSection';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';
import type {
  PricingDecisionExecutionResponse,
  PricingDecisionOptimizationResponse,
  PricingDecisionEvaluationArchiveResponse,
} from '../modules/marginCenter/types/pricingDecision';

type NormalizedResult = PricingDecisionExecutionResponse | PricingDecisionOptimizationResponse;
type CompleteArchive = Extract<PricingDecisionEvaluationArchiveResponse, { archive_status: 'complete' }>;
type PartialArchive = Extract<PricingDecisionEvaluationArchiveResponse, { archive_status: 'legacy_partial' }>;

const surface = {
  bgcolor: '#111d2e', color: '#e2e8f0',
  border: '1px solid rgba(148,163,184,0.22)',
  borderRadius: 3, backgroundImage: 'none',
};
const formatNumber = (v: number | null | undefined, digits = 2): string =>
  v == null || !Number.isFinite(v) ? 'No disponible'
    : new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(v);
const formatPct = (v: number | null | undefined, digits = 2): string =>
  v == null || !Number.isFinite(v) ? 'No disponible' : `${formatNumber(v, digits)} %`;
const formatQuantity = (v: number | null | undefined): string =>
  formatNumber(v, v != null && Number.isFinite(v) && v % 1 !== 0 ? 2 : 0);
const plain = (v: string | null | undefined): string => v == null || !v.trim() ? 'No disponible' : v;
const dateText = (v: string | null | undefined): string => {
  if (!v) return 'Fecha no disponible';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? 'Fecha no disponible' : d.toLocaleString('es-ES');
};
const record = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'string') {
    try { return record(JSON.parse(value) as unknown); } catch { return null; }
  }
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
};
const readString = (obj: Record<string, unknown> | null, key: string): string | null => {
  const value = obj?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};
const readNumber = (obj: Record<string, unknown> | null, key: string): number | null => {
  const value = obj?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const strategyLabel = (profile: string | null | undefined, sensitivity: number | null | undefined): string => {
  const labels: Record<string, string> = {
    conservador: 'Conservadora', equilibrado: 'Equilibrada', agresivo: 'Agresiva',
  };
  const mode = profile?.toLowerCase();
  if (mode === 'manual') {
    return sensitivity == null || !Number.isFinite(sensitivity)
      ? 'Manual · intensidad no registrada'
      : `Manual · ${formatNumber(sensitivity, sensitivity % 1 ? 1 : 0)} %`;
  }
  return labels[mode ?? ''] ?? 'No registrada';
};
const sourceLabel = (value: string | null | undefined): string => ({
  relationship: 'Desde una relación cliente-producto',
  customer: 'Desde un cliente',
  product: 'Desde un producto',
  optimization_hub: 'Nueva evaluación',
}[value ?? ''] ?? 'Evaluación contextual');
const riskLabel = (value: string | null | undefined): string => ({
  low: 'Precaución baja', medium: 'Precaución moderada', high: 'Precaución elevada',
}[value?.toLowerCase() ?? ''] ?? 'Sin clasificación disponible');
const reviewLabel = (value: string | null | undefined): string => ({
  evaluation_possible: 'Disponible para evaluación comercial',
  review_required: 'Requiere revisión comercial',
  reference_only: 'Solo como referencia',
}[value ?? ''] ?? 'Revisión no determinada');

function Panel({ title, eyebrow, children }: {
  title: string; eyebrow?: string; children: React.ReactNode;
}) {
  return <Paper sx={{ ...surface, p: { xs: 2.2, md: 2.8 }, minWidth: 0 }}>
    {eyebrow && <Typography variant="overline" color="#70d9ff" fontWeight={850} letterSpacing={1.1}>{eyebrow}</Typography>}
    <Typography variant="h6" color="#f8fafc" fontWeight={850} sx={{ mb: 2, letterSpacing: '-0.015em' }}>{title}</Typography>
    {children}
  </Paper>;
}
function Info({ label, value, hint, prominent = false }: {
  label: string; value: React.ReactNode; hint?: string; prominent?: boolean;
}) {
  return <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: '#93abc2', fontWeight: 680 }}>{label}</Typography>
    <Typography variant={prominent ? 'h6' : 'body1'} fontWeight={820} color="#edf6ff"
      sx={{ overflowWrap: 'anywhere', lineHeight: 1.3, mt: 0.35 }}>{value}</Typography>
    {hint && <Typography variant="caption" color="#829ab0" sx={{ display: 'block', mt: 0.4 }}>{hint}</Typography>}
  </Box>;
}
function DataGrid({ children, columns = 2 }: { children: React.ReactNode; columns?: 2 | 3 | 4 }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', md: `repeat(${columns},minmax(0,1fr))` }, gap: { xs: 1.6, md: 2.2 } }}>{children}</Box>;
}

function DecisionSummary({ archive }: { archive: CompleteArchive }) {
  const result: NormalizedResult = archive.response_snapshot;
  const context = 'context' in result ? result.context : null;
  const relationship = 'relationship' in result ? result.relationship : null;
  const customerId = context?.customer.customer_id ?? relationship?.customer_id ?? null;
  const productId = context?.product.product_id ?? relationship?.product_id ?? null;
  const recommendation = result.recommendation;
  return <Paper sx={{ ...surface, borderColor: '#286779', p: { xs: 2, md: 2.5 } }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(230px,1.1fr) minmax(160px,0.65fr) minmax(290px,1fr)' }, gap: 2, alignItems: 'center' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" color="#7dd3fc" fontWeight={800}>Cliente · Producto</Typography>
        <Typography variant="h6" color="#f7fbff" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>
          {context?.customer.state === 'new' ? 'Cliente nuevo' : plain(customerId)}
          {' · '}{context?.product.state === 'new' ? 'Producto nuevo' : plain(productId)}
        </Typography>
        <Typography variant="caption" color="#a7c2d3">Evaluación guardada el {dateText(archive.created_at)}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="#a7c2d3" fontWeight={800}>PRECIO QUE RECOMIENDA PROA</Typography>
        <Typography variant="h4" color="#a7f5e5" fontWeight={900} sx={{ letterSpacing: '-0.04em' }}>{formatNumber(recommendation.suggested_price)}</Typography>
        <Typography variant="caption" color="#a7c2d3">Moneda no registrada en el archivo</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 1.3 }}>
        <Info label="Coste empleado" value={formatNumber(recommendation.cost_used)} />
        <Info label="Margen sobre ingresos" value={formatPct(recommendation.suggested_margin_pct)} />
        <Info label="Estrategia" value={strategyLabel(result.request.strategy_profile, result.request.strategy_sensitivity)} />
      </Box>
    </Box>
  </Paper>;
}

function NormalizedEvaluation({ archive, showHeader = true }: { archive: CompleteArchive; showHeader?: boolean }) {
  const result: NormalizedResult = archive.response_snapshot;
  const context = 'context' in result ? result.context : null;
  const relationship = 'relationship' in result ? result.relationship : null;
  const mode = context?.mode ?? (relationship ? 'observed' : null);
  const customerId = context?.customer.customer_id ?? relationship?.customer_id ?? null;
  const productId = context?.product.product_id ?? relationship?.product_id ?? null;
  const r = result.recommendation;
  const s = result.scenarios;
  const elasticity = result.evidence.elasticity;
  const validPrices = [s.inertial_price, r.suggested_price, s.max_ai_price].filter(
    (v): v is number => v != null && Number.isFinite(v) && v > 0,
  );
  const topPrice = validPrices.length ? Math.max(...validPrices) : null;
  const strategy = strategyLabel(result.request.strategy_profile, result.request.strategy_sensitivity);
  const risk = riskLabel(result.risks.label);
  const review = reviewLabel(result.risks.review_mode);
  const diff = s.inertial_price != null && r.suggested_price != null
    && Number.isFinite(s.inertial_price) && Number.isFinite(r.suggested_price)
    ? r.suggested_price - s.inertial_price : null;
  const diffText = diff == null ? null : diff > 0
    ? `La recomendación supera en ${formatNumber(diff)} al escenario inercial.`
    : diff < 0 ? `La recomendación está ${formatNumber(Math.abs(diff))} por debajo del escenario inercial.`
      : 'La recomendación coincide con el escenario inercial.';
  const scenarios = [
    { key: 'inertial', label: 'Continuidad comercial', value: s.inertial_price, subtitle: 'Escenario inercial estimado', caption: 'Punto de comparación', accent: '#9cb8ce' },
    { key: 'recommended', label: 'Recomendación PROA', value: r.suggested_price, subtitle: 'Con la estrategia seleccionada', caption: 'Resultado calculado', accent: '#72e4d5' },
    { key: 'maximum', label: 'Escenario IA', value: s.max_ai_price, subtitle: 'Máximo estimado por el motor', caption: 'Simulación, no precio aplicado', accent: '#d8b8ff' },
  ];

  return <Stack spacing={2.2}>
    {showHeader && <Paper sx={{
      borderRadius: 3, p: { xs: 2.5, md: 3.5 }, overflow: 'hidden',
      background: 'linear-gradient(110deg, #12364a 0%, #12273c 48%, #17233c 100%)',
      border: '1px solid rgba(91,217,235,0.28)', position: 'relative',
    }}>
      <Box sx={{ position: 'absolute', width: 390, height: 390, right: -100, top: -180,
        borderRadius: '50%', border: '1px solid rgba(110,231,245,0.17)', pointerEvents: 'none' }} />
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
        <Box sx={{ minWidth: 0, position: 'relative' }}>
          <Typography variant="overline" color="#80e1f6" fontWeight={850} letterSpacing={1.2}>
            Resultado guardado · {dateText(archive.created_at)}
          </Typography>
          <Typography variant="h4" color="#f8fafc" fontWeight={850} sx={{ mt: 0.4, letterSpacing: '-0.03em' }}>
            {context?.customer.state === 'new' ? 'Cliente nuevo' : plain(customerId)}
            <Box component="span" sx={{ color: '#7adbec', mx: 1.2 }}>·</Box>
            {context?.product.state === 'new' ? 'Producto nuevo' : plain(productId)}
          </Typography>
          <Typography variant="body2" color="#b6cbda" sx={{ mt: 1 }}>
            {mode === 'prospective' ? 'Evaluación de una oportunidad prospectiva' : 'Evaluación de una relación observada'}
            {' · '}{sourceLabel(context?.source ?? (relationship ? 'relationship' : null))}
          </Typography>
        </Box>
        <Chip label="Evaluación guardada" color="success" sx={{ fontWeight: 800, position: 'relative', alignSelf: 'flex-start' }} />
      </Stack>
    </Paper>}

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.65fr) minmax(310px,0.85fr)' }, gap: 2.2, alignItems: 'start' }}>
      <Stack spacing={2.2} sx={{ minWidth: 0 }}>
        <Paper sx={{ ...surface, overflow: 'hidden', borderColor: '#286779' }}>
          <Box sx={{ px: { xs: 2.4, md: 3 }, py: { xs: 2.5, md: 3.2 }, background: 'linear-gradient(105deg, rgba(19,88,101,0.48),rgba(16,31,50,0.08))' }}>
            <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
              <TrendingUpRoundedIcon sx={{ color: '#7ce9d9' }} />
              <Typography variant="overline" color="#87ecdf" fontWeight={850} letterSpacing={1.2}>Resultado principal</Typography>
            </Stack>
            <Typography variant="body2" color="#d4e4ec">Precio recomendado para esta evaluación</Typography>
            <Typography component="div" sx={{ color: '#f8fafc', fontSize: { xs: 42, md: 58 }, fontWeight: 900, letterSpacing: '-0.06em', lineHeight: 1.16, my: 0.5 }}>
              {formatNumber(r.suggested_price)}
            </Typography>
            <Typography variant="caption" color="#a6bbc9">Importe sin símbolo monetario: la moneda contractual no consta en este archivo.</Typography>
            <Chip size="small" sx={{ display: 'block', width: 'fit-content', mt: 1.7, fontWeight: 800,
              bgcolor: '#1e4f54', color: '#a1fae9' }} label={result.status === 'ok' ? 'Recomendación calculada' : 'Sin recomendación'} />
          </Box>
          <Divider sx={{ borderColor: '#285363' }} />
          <Box sx={{ px: { xs: 2.4, md: 3 }, py: 2.5 }}>
            <DataGrid columns={3}>
              <Info label="Coste empleado" value={formatNumber(r.cost_used)} hint="Valor utilizado en el cálculo" prominent />
              <Info label="Margen sobre ingresos" value={formatPct(r.suggested_margin_pct)} hint="(Precio − coste) / precio" prominent />
              <Info label="Incremento sobre coste" value={formatPct(r.suggested_markup_pct)} hint="(Precio − coste) / coste" prominent />
            </DataGrid>
          </Box>
        </Paper>

        <Panel title="Tres escenarios para comparar" eyebrow="Alternativas calculadas">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' }, gap: 1.3 }}>
            {scenarios.map((sc) => <Box key={sc.key} sx={{ p: 2, bgcolor: sc.key === 'recommended' ? '#123f43' : '#0e192b',
              border: `1px solid ${sc.key === 'recommended' ? '#459e91' : '#30465b'}`, borderRadius: 2, minWidth: 0 }}>
              <Typography variant="caption" color={sc.accent} fontWeight={850}>{sc.label}</Typography>
              <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 870, mt: 0.6, mb: 0.3, letterSpacing: '-0.035em' }}>
                {formatNumber(sc.value)}
              </Typography>
              <Typography variant="caption" color="#a6bdd1">{sc.subtitle}</Typography>
              <Box sx={{ mt: 2, height: 6, borderRadius: 6, bgcolor: '#2c4154', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', borderRadius: 6, bgcolor: sc.accent,
                  width: topPrice != null && sc.value != null && Number.isFinite(sc.value) && sc.value >= 0
                    ? `${Math.min(100, sc.value / topPrice * 100)}%` : '0%' }} />
              </Box>
              <Typography variant="caption" color="#8ca4b9" sx={{ display: 'block', mt: 1 }}>{sc.caption}</Typography>
            </Box>)}
          </Box>
          {diffText && <Alert severity="info" icon={<BalanceRoundedIcon fontSize="small" />} sx={{ mt: 2, bgcolor: '#102b3b', color: '#d5e9f8' }}>
            {diffText} Esta diferencia es aritmética; no representa ventas ni resultados previstos.
          </Alert>}
        </Panel>

        <Panel title="Precios según la cantidad" eyebrow="Simulación por volumen">
          {s.pricing_matrix?.length ? <TableContainer>
            <Table size="small" sx={{ minWidth: 340 }}>
              <TableHead><TableRow sx={{ bgcolor: '#0d1a2b', '& th': { color: '#99b6cc', borderColor: '#30465b', fontWeight: 800 } }}>
                <TableCell>Cantidad evaluada</TableCell><TableCell align="right">Precio por unidad</TableCell><TableCell align="right">Variación frente a tarifa</TableCell>
              </TableRow></TableHead>
              <TableBody>{s.pricing_matrix.map((tier, i) => <TableRow key={`${tier.quantity}-${i}`} sx={{ '& td': { color: '#eaf5fd', borderColor: '#30465b' } }}>
                <TableCell>{formatQuantity(tier.quantity)}</TableCell>
                <TableCell align="right"><Typography fontWeight={800}>{formatNumber(tier.suggested_price)}</Typography></TableCell>
                <TableCell align="right">{typeof tier.suggested_discount === 'number' && Number.isFinite(tier.suggested_discount)
                  ? formatPct(tier.suggested_discount * 100) : 'No disponible'}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer> : <Typography variant="body2" color="#94a3b8">No se conserva una matriz de cantidades en esta evaluación.</Typography>}
          <Typography variant="caption" color="#95acbf" sx={{ display: 'block', mt: 1.5 }}>
            Estos importes son alternativas calculadas, no precios acordados ni ventas efectivas.
          </Typography>
        </Panel>
      </Stack>

      <Stack spacing={2.2} sx={{ minWidth: 0 }}>
        <Panel title="Cómo se configuró" eyebrow="Contexto comercial">
          <Stack spacing={2}>
            <Info label="Estrategia seleccionada" value={strategy} prominent />
            {typeof result.request.strategy_sensitivity === 'number' && Number.isFinite(result.request.strategy_sensitivity)
              && <Box>
                <Box sx={{ bgcolor: '#263b4f', height: 7, borderRadius: 8, overflow: 'hidden', mt: 0.5 }}>
                  <Box sx={{ width: `${Math.min(100, Math.max(0, result.request.strategy_sensitivity))}%`, bgcolor: '#67d8f2', height: '100%', borderRadius: 8 }} />
                </Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.45 }}>
                  <Typography variant="caption" color="#819caf">Más continuidad</Typography>
                  <Typography variant="caption" color="#819caf">Más señal IA</Typography>
                </Stack>
              </Box>}
            <Divider sx={{ borderColor: '#324457' }} />
            <DataGrid>
              <Info label="Cantidad evaluada" value={formatQuantity(result.request.quantity)} />
              <Info label="Matriz por cantidades" value={result.request.generate_pricing_matrix ? 'Solicitada' : 'No solicitada'} />
            </DataGrid>
            <Typography variant="caption" color="#91aabc">El porcentaje de intensidad no es un descuento ni un porcentaje de margen.</Typography>
          </Stack>
        </Panel>

        <Panel title="Qué significa el resultado" eyebrow="Lectura comercial">
          <Stack spacing={1.7}>
            <Stack direction="row" gap={1}><ArrowForwardRoundedIcon sx={{ color: '#7ad7e8', mt: 0.3 }} />
              <Typography variant="body2" color="#d7e9f4">El escenario inercial sirve como referencia de continuidad comercial; no constituye una venta observada.</Typography></Stack>
            <Stack direction="row" gap={1}><ArrowForwardRoundedIcon sx={{ color: '#7ad7e8', mt: 0.3 }} />
              <Typography variant="body2" color="#d7e9f4">La recomendación combina la evaluación del motor con la intensidad estratégica utilizada.</Typography></Stack>
            <Stack direction="row" gap={1}><ArrowForwardRoundedIcon sx={{ color: '#7ad7e8', mt: 0.3 }} />
              <Typography variant="body2" color="#d7e9f4">El escenario IA es una estimación alternativa. No debe interpretarse como un resultado comercial garantizado.</Typography></Stack>
            {s.haircut_vs_max_ai_pct != null && Number.isFinite(s.haircut_vs_max_ai_pct) &&
              <Box sx={{ p: 1.6, bgcolor: '#1b3248', borderRadius: 2 }}>
                <Info label="Ajuste frente al escenario IA" value={formatPct(s.haircut_vs_max_ai_pct)}
                  hint="Diferencia calculada por el motor al aplicar la estrategia." />
              </Box>}
          </Stack>
        </Panel>

        <Panel title="Evidencia y precauciones" eyebrow="Interpretación responsable">
          <Stack spacing={1.7}>
            <Info label="Revisión comercial" value={review} />
            <Info label="Nivel orientativo de precaución" value={risk} />
            <Divider sx={{ borderColor: '#324457' }} />
            <Typography variant="body2" fontWeight={790} color="#f5f8ff">Información sobre sensibilidad de la demanda</Typography>
            {elasticity.available ? <>
              <Info label="Estimación histórica disponible" value={formatNumber(elasticity.estimated_value)} />
              <Typography variant="body2" color="#bbcedd">
                {elasticity.reliable
                  ? elasticity.applied ? 'La señal de demanda se utilizó en el cálculo.' : 'La señal está disponible, pero no se aplicó a esta recomendación.'
                  : 'La fiabilidad de esta estimación no está acreditada. No debe utilizarse como certeza comercial.'}
              </Typography>
            </> : <Typography variant="body2" color="#b4c6d5">No hay una estimación de demanda disponible para esta evaluación.</Typography>}
            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ bgcolor: '#102839', color: '#d9ecfa' }}>
              Este apartado describe el cálculo original. Las decisiones humanas, si existen, se consultan en «Decisión comercial». Aquí no se acredita aplicación efectiva del precio ni resultado comercial posterior.
            </Alert>
          </Stack>
        </Panel>
      </Stack>
    </Box>
  </Stack>;
}

function LegacyEvaluation({ archive }: { archive: PartialArchive }) {
  const input = record(archive.legacy_input_payload);
  const output = record(archive.legacy_output_payload);
  const profile = readString(input, 'strategy_profile');
  const sensitivity = readNumber(input, 'strategy_sensitivity');
  const newCustomer = input?.is_new_client === true;
  const newProduct = input?.is_new_product === true;
  const customerId = readString(input, 'client_id');
  const productId = readString(input, 'product_id');
  const suggested = readNumber(output, 'suggested_price');
  const cost = readNumber(output, 'cost_used');
  return <Stack spacing={2.2}>
    <Alert severity="warning" sx={{ border: '1px solid #816d43', bgcolor: '#36291c', color: '#f8e2b8' }}>
      Este registro es anterior al archivo contextual completo. Se muestran únicamente los datos que se conservaron;
      no es posible recuperar retrospectivamente la explicación o los escenarios originales. No se vuelve a ejecutar el modelo.
    </Alert>
    <Paper sx={{ ...surface, p: { xs: 2.5, md: 3 }, background: 'linear-gradient(100deg,#293044,#131f32)' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Inventory2OutlinedIcon sx={{ color: '#e4c88f' }} />
        <Typography variant="overline" fontWeight={800} color="#e4c88f">Información disponible · {dateText(archive.created_at)}</Typography>
      </Stack>
      <DataGrid columns={3}>
        <Info label="Cliente" value={newCustomer ? 'Cliente nuevo' : plain(customerId)} />
        <Info label="Producto" value={newProduct ? 'Producto nuevo' : plain(productId)} />
        <Info label="Estrategia" value={strategyLabel(profile, sensitivity)} />
        <Info label="Precio calculado" value={formatNumber(suggested)} hint="Sin confirmación de aplicación" prominent />
        <Info label="Coste usado en el cálculo" value={formatNumber(cost)} />
        <Info label="Cantidad evaluada" value={formatQuantity(readNumber(input, 'quantity'))} />
      </DataGrid>
    </Paper>
    <Panel title="Qué no puede reconstruirse">
      <Typography color="#b6cadb" variant="body2">
        Este registro no conserva la ficha comercial normalizada, la comparación completa de escenarios ni las explicaciones
        en el formato actual. Los datos históricos se mantienen intactos para su trazabilidad interna, sin presentarte códigos o archivos técnicos.
      </Typography>
    </Panel>
  </Stack>;
}

const OptimizationEvaluationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { optimizationId } = useParams<{ optimizationId: string }>();
  const state = location.state as { returnTo?: string; historyScrollY?: number } | null;
  // PRC-028: el origen viaja en la dirección (`returnTo`); `state` por compatibilidad.
  const requestedReturnTo = new URLSearchParams(location.search).get(RETURN_PARAM) ?? state?.returnTo;
  // PRC-027: el origen puede llevar búsqueda (p. ej. la guía); el botón nombra su destino.
  const returnTo = requestedReturnTo?.startsWith('/optimization-center')
    || requestedReturnTo?.startsWith('/optimization/history')
    ? requestedReturnTo
    : '/optimization/history';
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['pricingDecisionEvaluation', optimizationId],
    queryFn: () => getPricingDecisionEvaluation(optimizationId!),
    enabled: Boolean(optimizationId), retry: 1,
  });

  return <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3, xl: 5 }, pt: 3, pb: 6 }}>
    <Stack spacing={2.2}>
      {/* PRC-032: cabecera común; el botón nombra su destino (PRC-027/028). */}
      <PageHeader
        eyebrow="Optimización"
        title="Evaluación y decisión"
        back={{ label: backLabel(returnTo), onClick: () => navigate(returnTo, { state: { restoreScrollY: state?.historyScrollY } }) }}
        sx={{ mb: 0 }}
      />
      {/* PRC-031: forma de la evaluación mientras carga. */}
      {isLoading && <PageSkeleton variant="detail" bare padded={false} />}
      {isError && <Alert severity="error" action={<Button onClick={() => void refetch()} color="inherit">Reintentar</Button>}>
        No se pudo recuperar esta evaluación. Puede no existir o no estar disponible para tu cuenta.
      </Alert>}
      {data && (data.archive_status === 'complete' ? <>
        <DecisionSummary archive={data} />
        <OptimizationDecisionSection
          optimizationId={data.optimization_id}
          hasRecommendation={data.response_snapshot.status === 'ok'
            && data.response_snapshot.recommendation.suggested_price !== null
            && Number.isFinite(data.response_snapshot.recommendation.suggested_price)
            && data.response_snapshot.recommendation.suggested_price > 0}
        />
        <OptimizationApplicationSection
          optimizationId={data.optimization_id}
          eligibleObserved={'context' in data.response_snapshot
            ? data.response_snapshot.context.mode === 'observed'
              && data.response_snapshot.context.customer.state === 'existing'
              && data.response_snapshot.context.product.state === 'existing'
            : true}
        />
        <Accordion disableGutters sx={{ ...surface, '&:before': { display: 'none' }, overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#9ad5e9' }} />} sx={{ px: { xs: 2, md: 2.5 } }}>
            <Box>
              <Typography fontWeight={800} color="#f3faff">Ver detalle del cálculo</Typography>
              <Typography variant="caption" color="#9cb5c8">Escenarios, matriz de cantidades, estrategia y evidencia.</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, md: 1.5 }, pt: 0, pb: 1.5 }}>
            <NormalizedEvaluation archive={data} showHeader={false} />
          </AccordionDetails>
        </Accordion>
      </> : <LegacyEvaluation archive={data} />)}
      {data && <Typography variant="caption" color="#8098ac" sx={{ display: 'block', pt: 0.5 }}>
        Consulta de una evaluación pasada. El histórico no ejecuta un nuevo cálculo ni registra cambios de precio.
      </Typography>}
    </Stack>
  </Box>;
};

export default OptimizationEvaluationPage;
